/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-19
 */

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Block as BlockIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Props for the LimitWarning component
 */
interface LimitWarningProps {
  /**
   * Type of limit being warned about
   */
  type: 'studio' | 'user';
  /**
   * Current count of the resource
   */
  currentCount: number;
  /**
   * Maximum allowed count (0 means unlimited)
   */
  maxCount: number;
  /**
   * Percentage threshold for showing warnings (default: 80)
   */
  warningThreshold?: number;
  /**
   * Custom message to display
   */
  customMessage?: string;
  /**
   * Whether to show upgrade button
   */
  showUpgradeButton?: boolean;
  /**
   * Custom upgrade button text
   */
  upgradeButtonText?: string;
  /**
   * Callback when upgrade button is clicked
   */
  onUpgrade?: () => void;
  /**
   * Additional styling
   */
  sx?: any;
}

/**
 * Reusable component for displaying subscription limit warnings
 *
 * @param props - Component props
 * @returns Warning component for subscription limits
 *
 * @example
 * <LimitWarning
 *   type="studio"
 *   currentCount={4}
 *   maxCount={5}
 *   showUpgradeButton
 *   onUpgrade={() => navigate('/pricing')}
 * />
 */
const LimitWarning: React.FC<LimitWarningProps> = ({
  type,
  currentCount,
  maxCount,
  warningThreshold = 80,
  customMessage,
  showUpgradeButton = true,
  upgradeButtonText = 'Upgrade Plan',
  onUpgrade,
  sx,
}) => {
  const { t } = useTranslation('billing');
  const theme = useTheme();
  const navigate = useNavigate();

  // Handle unlimited plans
  if (maxCount === 0) {
    return null; // No warnings for unlimited plans
  }

  // Calculate usage percentage
  const usagePercent = Math.min(100, (currentCount / maxCount) * 100);
  const isAtLimit = currentCount >= maxCount;
  const isNearLimit = usagePercent >= warningThreshold;

  // Don't show warning if not near limit
  if (!isNearLimit && !isAtLimit) {
    return null;
  }

  // Determine severity and messaging
  const severity = isAtLimit ? 'error' : 'warning';
  const icon = isAtLimit ? <BlockIcon /> : <WarningIcon />;

  const resourceName =
    type === 'studio' ? t('features.studio') : t('features.user');
  const resourcePlural =
    type === 'studio' ? t('features.studios') : t('features.users');

  // Generate default messages
  let title: string;
  let message: string;

  if (isAtLimit) {
    title = t('limits.limitReached');
    message = customMessage || t('limits.upgradeToUnlock');
  } else {
    title = t('limits.approachingLimit');
    message = customMessage || t('limits.avoidInterruptions');
  }

  // Default upgrade handler
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/pricing');
    }
  };

  return (
    <Alert
      severity={severity}
      icon={icon}
      sx={{
        borderRadius: 2,
        ...sx,
      }}
    >
      <AlertTitle sx={{ fontWeight: 600 }}>{title}</AlertTitle>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {currentCount} {t('usage.of')} {maxCount}{' '}
            {maxCount === 1 ? resourceName : resourcePlural} {t('usage.used')}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('usage.percentageUsed', { percent: Math.round(usagePercent) })}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={usagePercent}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.palette.grey[200],
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              backgroundColor: (() => {
                if (isAtLimit) {
                  return theme.palette.error.main;
                }
                if (usagePercent >= 90) {
                  return theme.palette.warning.main;
                }
                return theme.palette.info.main;
              })(),
            },
          }}
        />
      </Box>

      {/* Message */}
      <Typography variant="body2" sx={{ mb: showUpgradeButton ? 2 : 0 }}>
        {message}
      </Typography>

      {/* Upgrade Button */}
      {showUpgradeButton && (
        <Button
          variant={isAtLimit ? 'contained' : 'outlined'}
          color={isAtLimit ? 'error' : 'warning'}
          size="small"
          startIcon={<UpgradeIcon />}
          onClick={handleUpgrade}
          sx={{
            fontWeight: 600,
          }}
        >
          {upgradeButtonText}
        </Button>
      )}
    </Alert>
  );
};

export default LimitWarning;
